import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

import { serviceTermsService } from '@/services/providers';
import { planQueries } from '@/services/plans/queries';
import { toast } from 'sonner';

interface ServiceTermsManagementProps {
  providerId: number;
  versionId: number;
  regionId: number;
}

interface ServiceTermsData {
  contract_required?: string | null;
  cancellation_policy?: string | null;
  installation_required?: boolean | null;
  price_lock_period_months?: number | null;
}

interface Plan {
  id: number;
  name: string;
  channel_count?: number;
}

export function ServiceTermsManagement({ 
  providerId, 
  versionId, 
  regionId 
}: ServiceTermsManagementProps) {
  const [appliesTo, setAppliesTo] = useState<'provider' | 'plan' | 'category' | 'package'>('provider');
  const [selectedPlans, setSelectedPlans] = useState<number[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [providerData, setProviderData] = useState<ServiceTermsData>({});
  const [planData, setPlanData] = useState<ServiceTermsData>({});
  const [loading, setLoading] = useState(false);
  const [providerTemplateSaved, setProviderTemplateSaved] = useState(false);
  const [planOverridesSaved, setPlanOverridesSaved] = useState<Record<number, boolean>>({});

  // Load existing data
  useEffect(() => {
    loadData();
  }, [providerId, versionId, regionId]);

  // Auto-switch to plan mode when plan overrides exist (after refresh)
  useEffect(() => {
    if (Object.keys(planOverridesSaved).length > 0 && appliesTo === 'provider') {
      setAppliesTo('plan');
    }
  }, [planOverridesSaved, appliesTo]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load existing provider template
      const template = await serviceTermsService.getProviderTemplate(providerId, versionId, regionId);
      if (template) {
        setProviderData(template);
        setProviderTemplateSaved(true);
        // Initialize plan data with provider data
        setPlanData({ ...template });
      }

      // Load plans
      const plansData = await planQueries.getPlans(providerId, versionId);
      setPlans(plansData || []);

      // Load existing plan-specific data if any
      if (plansData && plansData.length > 0) {
        // Get all service terms for this provider/version/region
        // Load existing plan overrides from override tables if any
        const planIds = plansData.map((plan: any) => plan.id);
        const planOverrides = await serviceTermsService.getPlanOverrides(planIds);
        
        if (planOverrides && planOverrides.length > 0) {
          // Mark plans with overrides as saved
          const savedPlans = planOverrides.map((override: any) => override.plan_id) as number[];
          const savedState: Record<number, boolean> = {};
          savedPlans.forEach((planId: number) => {
            savedState[planId] = true;
          });
          setPlanOverridesSaved(savedState);
          
          // If there are plan overrides, select those plans but keep current mode
          if (savedPlans.length > 0) {
            setSelectedPlans(savedPlans as number[]);
            
            // Load the first plan's override data to show in the form
            const firstPlanOverride = planOverrides[0];
            setPlanData({
              contract_required: firstPlanOverride.contract_required,
              cancellation_policy: firstPlanOverride.cancellation_policy,
              installation_required: firstPlanOverride.installation_required,
              price_lock_period_months: firstPlanOverride.price_lock_period_months,
            });
          }
        }
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error("Failed to load existing data", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProviderTemplateSave = async () => {
    setLoading(true);
    try {
      // Use upsert to handle both create and update
      const result = await serviceTermsService.upsertProviderTemplate({
        provider_id: providerId,
        version_id: versionId,
        region_id: regionId,
        ...providerData
      });
      
      console.log('Provider template saved successfully:', result);
      setProviderTemplateSaved(true);
      
      // Copy provider data to plan data
      setPlanData({ ...providerData });

      toast.success("Provider template saved successfully", {
        style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
      });
    } catch (error) {
      console.error('Error saving provider template:', error);
      
      // Handle specific error cases
      if (error instanceof Error) {
        if (error.message.includes('duplicate key value violates unique constraint')) {
          toast.success("Service terms updated successfully (existing record was modified)", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else if (error.message.includes('23505')) {
          toast.success("Service terms updated successfully", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else {
          toast.error(error.message, {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        }
      } else {
        toast.error("Failed to save provider template", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlanTemplateSave = async () => {
    if (selectedPlans.length === 0) {
      toast.error("Please select at least one plan", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
      return;
    }

    setLoading(true);
    try {
      // Save overrides for all selected plans
      const planNames = selectedPlans.map(id => plans.find(p => p.id === id)?.name).filter(Boolean);
      
      for (const planId of selectedPlans) {
        try {
          const result = await serviceTermsService.upsertPlanOverride(planId, planData);
          console.log(`Plan override saved for plan ${planId}:`, result);
          
          // Mark this plan as saved
          setPlanOverridesSaved(prev => ({ ...prev, [planId]: true }));
        } catch (planError) {
          console.error(`Error saving override for plan ${planId}:`, planError);
          throw new Error(`Failed to save override for plan ${planId}: ${planError instanceof Error ? planError.message : 'Unknown error'}`);
        }
      }

      toast.success(`Plan overrides saved for: ${planNames.join(', ')}`, {
        style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
      });
    } catch (error) {
      console.error('Error saving plan template:', error);
      
      // Enhanced error handling with different colors
      if (error instanceof Error) {
        if (error.message.includes('duplicate key value violates unique constraint')) {
          toast.success("Plan overrides updated successfully", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else if (error.message.includes('23505')) {
          toast.success("Plan overrides updated successfully", {
            style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
          });
        } else {
          toast.error(error.message, {
            style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
          });
        }
      } else {
        toast.error("Failed to save plan template", {
          style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const resetPlanToProviderTemplate = () => {
    setPlanData({ ...providerData });
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Service Terms Management</h2>
          <p className="text-muted-foreground">Configure service terms and conditions for this provider.</p>
        </div>
        <Badge variant="outline">
          {appliesTo === 'provider' ? 'Provider Template' : 'Plan Overrides'}
        </Badge>
      </div>

      {/* Applies To Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Applies To</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={appliesTo}
            onValueChange={(value: 'provider' | 'plan' | 'category' | 'package') => setAppliesTo(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="provider">Provider-wide</SelectItem>
              <SelectItem value="plan">Plan-wise</SelectItem>
              <SelectItem value="category">Category-wise</SelectItem>
              <SelectItem value="package">Package-wise</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Provider Template Component (Always shown) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Template Configuration</CardTitle>
              <p className="text-sm text-muted-foreground">
                These values will apply to all plans by default.
              </p>
            </div>
            {providerTemplateSaved && (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                ✓ Saved
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <ServiceTermsForm
            data={providerData}
            onChange={setProviderData}
            disabled={false}
          />
          <div className="flex justify-end mt-4">
            <Button 
              onClick={handleProviderTemplateSave}
              disabled={loading}
              variant={providerTemplateSaved ? "outline" : "default"}
            >
              {loading ? 'Saving...' : providerTemplateSaved ? 'Update Template' : 'Save Template'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Plan Template Component (Shown when plan-wise is selected OR when plan overrides exist) */}
      {(appliesTo === 'plan' || Object.keys(planOverridesSaved).length > 0) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Plan Template Configuration</CardTitle>
              <div className="flex gap-2">
                {selectedPlans.some(planId => planOverridesSaved[planId]) && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    ✓ Overrides Saved
                  </Badge>
                )}
                {providerTemplateSaved && !selectedPlans.some(planId => planOverridesSaved[planId]) && (
                  <Badge variant="secondary">Editable</Badge>
                )}
                {!providerTemplateSaved && (
                  <Badge variant="outline">Read-only until provider template saved</Badge>
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              {providerTemplateSaved 
                ? 'Edit these values to override the provider template for selected plans.'
                : 'Save the provider template first to enable plan-specific overrides.'
              }
            </p>
            
            {/* Plan Selection Warning */}
            {selectedPlans.length === 0 && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-yellow-800 font-medium">
                    ⚠️ Please select at least one plan to configure plan-specific service terms
                  </span>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {/* Plan Selection */}
            <div className="mb-6 space-y-2">
              <Label>Select Plans for Override</Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {plans.length === 0 ? (
                  <p className="text-sm text-muted-foreground col-span-2">No plans available</p>
                ) : (
                  plans.map((plan) => (
                    <div key={plan.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`plan-${plan.id}`}
                        checked={selectedPlans.includes(plan.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPlans([...selectedPlans, plan.id]);
                          } else {
                            setSelectedPlans(selectedPlans.filter(id => id !== plan.id));
                          }
                        }}
                      />
                      <Label htmlFor={`plan-${plan.id}`} className="text-sm flex items-center gap-2">
                        {plan.name}
                        {planOverridesSaved[plan.id] && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                            ✓
                          </Badge>
                        )}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Plan Template Form */}
            <ServiceTermsForm
              data={planData}
              onChange={setPlanData}
              disabled={!providerTemplateSaved}
            />
            
            {providerTemplateSaved && (
              <div className="flex justify-between mt-4">
                <Button 
                  variant="outline"
                  onClick={resetPlanToProviderTemplate}
                >
                  Reset to Provider Template
                </Button>
                <Button 
                  onClick={handlePlanTemplateSave}
                  disabled={loading || selectedPlans.length === 0}
                  variant={selectedPlans.some(planId => planOverridesSaved[planId]) ? "outline" : "default"}
                  className="transition-all duration-200 ease-in-out hover:scale-105"
                >
                  {loading ? 'Saving...' : selectedPlans.some(planId => planOverridesSaved[planId]) ? 'Update Plan Overrides' : 'Save Plan Template'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface ServiceTermsFormProps {
  data: ServiceTermsData;
  onChange: (data: ServiceTermsData) => void;
  disabled?: boolean;
}

function ServiceTermsForm({ data, onChange, disabled }: ServiceTermsFormProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="contract_required">Contract Required</Label>
          <Select
            value={data.contract_required || ''}
            onValueChange={(value) => onChange({ ...data, contract_required: value })}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select contract requirement" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Contract</SelectItem>
              <SelectItem value="1_year">1 Year Contract</SelectItem>
              <SelectItem value="2_year">2 Year Contract</SelectItem>
              <SelectItem value="flexible">Flexible Terms</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="cancellation_policy">Cancellation Policy</Label>
          <Select
            value={data.cancellation_policy || ''}
            onValueChange={(value) => onChange({ ...data, cancellation_policy: value })}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select cancellation policy" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="anytime">Cancel Anytime</SelectItem>
              <SelectItem value="30_days">30 Days Notice</SelectItem>
              <SelectItem value="fee_required">Cancellation Fee Required</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="price_lock_period_months">Price Lock Period (months)</Label>
          <Input
            type="number"
            value={data.price_lock_period_months || ''}
            onChange={(e) => onChange({ 
              ...data, 
              price_lock_period_months: e.target.value ? parseInt(e.target.value) : null 
            })}
            disabled={disabled}
            placeholder="e.g., 12"
          />
        </div>

        <div className="flex items-center space-x-2 pt-8">
          <Switch
            checked={data.installation_required || false}
            onCheckedChange={(checked) => onChange({ ...data, installation_required: checked })}
            disabled={disabled}
          />
          <Label>Installation Required</Label>
        </div>
      </div>
    </div>
  );
}